import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ApiError, getConfig } from '@/lib/api'
import { CommandKind, type BotConfigDto, type EngineState } from '@/lib/types'
import { useAppStore } from '@/store/useAppStore'
import {
  SETTINGS_GROUPS,
  SETTINGS_KEYS,
  configValueToString,
  fieldInputToPayload,
  isFieldChanged,
  isProtectiveTightened,
  type FieldMeta,
} from './model'
import { InfoHint } from '@/components/common/InfoHint'
import { Spinner } from '@/components/common/Spinner'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { SettingsIcon } from '@/components/common/icons'
import type { AutobotHook } from './useAutobot'

interface BotSettingsProps {
  engine: EngineState
  run: AutobotHook['run']
  pending: string | null
}

const FIELD_BY_KEY = new Map<string, FieldMeta>(
  SETTINGS_GROUPS.flatMap((g) => g.fields).map((f) => [f.key, f]),
)

export function BotSettings({ engine, run, pending }: BotSettingsProps) {
  const [config, setConfig] = useState<BotConfigDto | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const mounted = useRef(true)

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const cfg = await getConfig(signal)
      if (!mounted.current) return
      setConfig(cfg)
      const next: Record<string, string> = {}
      for (const key of SETTINGS_KEYS) next[key] = configValueToString(cfg[key])
      setValues(next)
      setError(null)
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      if (err instanceof ApiError && err.status === 401) {
        useAppStore.getState().logout()
        return
      }
      if (mounted.current) setError(err instanceof ApiError ? err.detail : 'Не удалось загрузить настройки')
    }
  }, [])

  useEffect(() => {
    mounted.current = true
    setLoading(true)
    void load().finally(() => {
      if (mounted.current) setLoading(false)
    })
    return () => {
      mounted.current = false
    }
  }, [load])

  const setValue = (key: string, raw: string) => setValues((v) => ({ ...v, [key]: raw }))

  // Изменённые поля (относительно загруженного конфига).
  const changedKeys = useMemo(() => {
    if (!config) return []
    return SETTINGS_KEYS.filter((key) => {
      const meta = FIELD_BY_KEY.get(key)
      if (!meta || meta.kind === 'readonly') return false
      return isFieldChanged(meta, values[key] ?? '', configValueToString(config[key]))
    })
  }, [config, values])

  // Анти-стампед: ужесточение защитного порога при открытых связках требует подтверждения.
  const tightenedProtective = useMemo(() => {
    if (!config || engine.active_trades.length === 0) return []
    return changedKeys.filter((key) => {
      const meta = FIELD_BY_KEY.get(key)
      return meta && isProtectiveTightened(meta, values[key] ?? '', configValueToString(config[key]))
    })
  }, [config, changedKeys, values, engine.active_trades.length])

  const buildPayload = useCallback((): Record<string, unknown> => {
    const payload: Record<string, unknown> = {}
    for (const key of changedKeys) {
      const meta = FIELD_BY_KEY.get(key)
      if (meta) payload[key] = fieldInputToPayload(meta, values[key] ?? '')
    }
    return payload
  }, [changedKeys, values])

  const doSave = useCallback(async () => {
    const ok = await run('save', CommandKind.SAVE_CONFIG, buildPayload())
    if (ok) await load() // перечитать применённые/нормализованные значения
  }, [run, buildPayload, load])

  const onSaveClick = () => {
    if (changedKeys.length === 0) return
    if (tightenedProtective.length > 0) setConfirmOpen(true)
    else void doSave()
  }

  const dirty = changedKeys.length > 0
  const saving = pending === 'save'

  return (
    <div className="panel">
      <div className="panel__head">
        <div>
          <div className="panel__title">Настройки бота</div>
          <div className="panel__subtitle">
            Все поля §1.11 · защитные пороги применяются к открытым связкам сразу, входные — к новым
          </div>
        </div>
        <div className="panel__head-controls">
          {dirty ? <span className="badge badge--accent">изменено: {changedKeys.length}</span> : null}
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={!dirty || saving || loading}
            onClick={onSaveClick}
          >
            {saving ? <Spinner size={14} className="btn__spin" /> : <SettingsIcon size={15} />}
            Сохранить
          </button>
        </div>
      </div>

      <div className="panel__pad">
        {loading ? (
          <div className="empty">
            <Spinner size={24} />
            <div className="empty__text">Загрузка настроек…</div>
          </div>
        ) : error ? (
          <div className="note note--warn">{error}</div>
        ) : (
          <>
            <div className="settings-legend">
              <span className="settings-legend__dot settings-legend__dot--prot" /> защитный порог
              (горячо к открытым связкам)
            </div>
            {SETTINGS_GROUPS.map((group) => (
              <fieldset key={group.title} className="settings-group">
                <legend className="settings-group__title">{group.title}</legend>
                <div className="settings-grid">
                  {group.fields.map((meta) => (
                    <SettingField
                      key={meta.key}
                      meta={meta}
                      value={values[meta.key] ?? ''}
                      original={config ? configValueToString(config[meta.key]) : ''}
                      onChange={(raw) => setValue(meta.key, raw)}
                    />
                  ))}
                </div>
              </fieldset>
            ))}
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Ужесточение защитного порога"
        message={
          <>
            Вы ужесточаете защитный порог при <strong>{engine.active_trades.length}</strong> открытых
            связках — изменение применится к ним <strong>немедленно</strong> и может вызвать выход/
            снижение экспозиции. Поля: {tightenedProtective.join(', ')}. Продолжить?
          </>
        }
        confirmLabel="Применить"
        busy={saving}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false)
          void doSave()
        }}
      />
    </div>
  )
}

interface SettingFieldProps {
  meta: FieldMeta
  value: string
  original: string
  onChange: (raw: string) => void
}

function SettingField({ meta, value, original, onChange }: SettingFieldProps) {
  const changed = meta.kind !== 'readonly' && isFieldChanged(meta, value, original)
  return (
    <div className={`field settings-field${changed ? ' is-changed' : ''}`}>
      <div className="field__labelrow">
        <span className="field__label">{meta.label}</span>
        {meta.protective ? (
          <span className="settings-prot" title="Защитный порог — применяется к открытым связкам сразу" />
        ) : null}
        <InfoHint text={meta.hint} />
      </div>
      {meta.kind === 'number' ? (
        // Строковый буфер (не Number): конфиг — Decimal-строки, и round-trip через JS Number
        // округлил бы значение (§1.2: фронт не теряет точность денег/ставок). Бэкенд коэрсит
        // строку в Decimal/int. Для int-полей — только цифры (без точки): иначе int("5.0") на
        // бэке бросит ValueError. Для Decimal — цифры/одна точка/ведущий минус.
        <div className="input">
          <input
            id={`set-${meta.key}`}
            type="text"
            inputMode={meta.integer ? 'numeric' : 'decimal'}
            autoComplete="off"
            value={value}
            onChange={(e) => {
              const raw = e.target.value
              const ok = meta.integer ? /^\d*$/.test(raw) : /^-?\d*\.?\d*$/.test(raw)
              if (ok) onChange(raw)
            }}
          />
          {meta.suffix ? <span className="input__suffix">{meta.suffix}</span> : null}
        </div>
      ) : meta.kind === 'select' ? (
        <select
          id={`set-${meta.key}`}
          className="mini-select settings-select"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        >
          {(meta.options ?? []).map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
      ) : meta.kind === 'text' ? (
        <div className="input">
          <input
            id={`set-${meta.key}`}
            type="text"
            autoComplete="off"
            value={value}
            placeholder="через запятую"
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      ) : (
        <div className="input input--readonly">
          <input id={`set-${meta.key}`} type="text" value={value || 'taker'} readOnly disabled />
        </div>
      )}
    </div>
  )
}
